import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ThumbsUp, ThumbsDown, Filter, Loader2, Sparkles } from "lucide-react";

/**
 * Admin answer-review queue for the account assistant.
 *
 * This surface renders governed improvement evidence only — every
 * verdict/reason edit round-trips to `/api/admin/account-assistant/feedback`
 * (upsert on (reviewer_user_id, assistant_message_id)) and MUST NOT
 * mutate the underlying assistant message, thread, KB doc, prompt or
 * outbound system. Access is gated at the backend to `admin` and
 * `super_admin` only — this page defensively rechecks the response
 * status but does NOT trust client-side role state.
 */
export default function AdminAccountAssistantReviewPage() {
  // status: all | unreviewed | good | bad — mirrors the review-queue
  // endpoint filter. Unreviewed surfaces fresh assistant answers that
  // no reviewer has rated yet.
  const [statusFilter, setStatusFilter] = useState("unreviewed");
  const [voiceFilter, setVoiceFilter]   = useState("all");   // all | flagged
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);      // queue row
  const [context, setContext] = useState(null);        // { assistant_message, prior_user_message, thread, my_review }
  const [reason, setReason] = useState("");
  const [correction, setCorrection] = useState("");
  const [saving, setSaving] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [domains, setDomains] = useState([]);
  const [promoteDomain, setPromoteDomain] = useState("");

  const loadQueue = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/admin/account-assistant/review-queue?status=${statusFilter}&voice=${voiceFilter}&limit=100`,
      );
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (err) {
      if (err?.response?.status === 403) toast.error("Admin access required");
      else toast.error("Couldn't load review queue");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadQueue(); /* eslint-disable-next-line */ }, [statusFilter, voiceFilter]);

  // Preload KB domains so the "Promote to KB" flow can offer a
  // classification without a second click-through.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/kb/domains");
        setDomains((data?.domains || []).filter((d) => d.enabled));
      } catch { /* non-blocking */ }
    })();
  }, []);

  const openMessage = async (messageId, prefillRow) => {
    setSelected(prefillRow || null);
    setContext(null);
    setReason(prefillRow?.current_reason || "");
    setCorrection(prefillRow?.current_proposed_correction || "");
    try {
      const { data } = await api.get(`/admin/account-assistant/messages/${messageId}`);
      setContext(data);
      // If the caller-admin previously reviewed this message, preload
      // their verdict/reason so the "revise" flow lands ergonomically.
      if (data.my_review) {
        setReason(data.my_review.reason || "");
        setCorrection(data.my_review.proposed_correction || "");
      }
    } catch (err) {
      toast.error("Couldn't load message context");
    }
  };

  const submit = async (verdict) => {
    if (!context?.assistant_message?.id) return;
    if (verdict === "bad" && !reason.trim()) {
      toast.error("A reason is required for a Bad review");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/account-assistant/feedback", {
        assistant_message_id: context.assistant_message.id,
        verdict,
        reason: reason.trim(),
        proposed_correction: correction.trim(),
      });
      toast.success(`Saved as ${verdict.toUpperCase()}`);
      await loadQueue();
      await openMessage(context.assistant_message.id, {
        ...(selected || {}),
        review_status: verdict,
        current_reason: reason.trim(),
        current_proposed_correction: correction.trim(),
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save review");
    } finally {
      setSaving(false);
    }
  };

  const promoteToKb = async () => {
    if (!context?.assistant_message?.id) return;
    setPromoting(true);
    try {
      const { data } = await api.post("/admin/account-assistant/promote-to-kb", {
        assistant_message_id: context.assistant_message.id,
        title: "",              // let the backend derive a title
        domain: promoteDomain || null,
      });
      toast.success(
        data.domain
          ? `Added to ${data.domain} knowledge (${data.kb_document_id.slice(0,8)}…)`
          : `Added to unclassified KB queue (${data.kb_document_id.slice(0,8)}…)`,
      );
      await openMessage(context.assistant_message.id, selected);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't promote to KB");
    } finally {
      setPromoting(false);
    }
  };

  const groupedCount = useMemo(() => ({
    unreviewed: rows.filter((r) => r.review_status === "unreviewed").length,
    good:       rows.filter((r) => r.review_status === "good").length,
    bad:        rows.filter((r) => r.review_status === "bad").length,
    voice_bad:  rows.filter((r) => r.voice_verdict === "bad").length,
  }), [rows]);

  return (
    <AppShell>
      <PageHero
        eyebrow="Admin"
        title="Assistant answer review"
        description="Mark Warren's answers Good or Bad with a reason. Stored as governed improvement evidence — never auto-changes the KB, prompt or model."
      />
      <div className="w-full pb-16">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Filter className="w-4 h-4 text-muted-foreground" />
          {["unreviewed", "all", "good", "bad"].map((v) => (
            <button key={v}
              onClick={() => setStatusFilter(v)}
              data-testid={`review-filter-${v}`}
              className={[
                "text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full transition",
                statusFilter === v ? "bg-primary text-white" : "text-muted-foreground hover:text-shell-foreground",
              ].join(" ")}>
              {v} {v !== "all" && `(${groupedCount[v] || 0})`}
            </button>
          ))}
          <span className="mx-2 h-4 w-px bg-neutral-300" aria-hidden />
          {["all", "flagged"].map((v) => (
            <button key={`voice-${v}`}
              onClick={() => setVoiceFilter(v)}
              data-testid={`review-voice-filter-${v}`}
              className={[
                "text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full transition",
                voiceFilter === v
                  ? (v === "flagged" ? "bg-rose-600 text-white" : "bg-neutral-800 text-white")
                  : "text-muted-foreground hover:text-shell-foreground",
              ].join(" ")}>
              Voice · {v} {v === "flagged" && `(${groupedCount.voice_bad || 0})`}
            </button>
          ))}
          <button onClick={loadQueue} className="ml-auto text-xs text-muted-foreground hover:text-primary" data-testid="review-refresh">
            Refresh
          </button>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          {/* Queue */}
          <div className="surface p-3 max-h-[70vh] overflow-y-auto" data-testid="review-queue">
            {loading && <p className="text-sm text-muted-foreground p-3">Loading…</p>}
            {!loading && rows.length === 0 && (
              <p className="text-sm text-muted-foreground p-3">
                {statusFilter === "unreviewed"
                  ? "No fresh unreviewed answers right now — new founder Q&A will surface here."
                  : "No reviews match this filter yet."}
              </p>
            )}
            {rows.map((r) => {
              const status = r.review_status || "unreviewed";
              const badgeCls = status === "good"
                ? "bg-emerald-100 text-emerald-800"
                : status === "bad"
                  ? "bg-rose-100 text-rose-800"
                  : "bg-neutral-100 text-neutral-700";
              const ts = r.review_updated_at || r.message_created_at;
              return (
                <button key={r.assistant_message_id}
                  onClick={() => openMessage(r.assistant_message_id, r)}
                  data-testid={`review-row-${r.assistant_message_id}`}
                  className={[
                    "w-full text-left rounded-lg border p-3 mb-2 transition",
                    selected?.assistant_message_id === r.assistant_message_id
                      ? "border-primary bg-primary/5"
                      : "border-shell-border hover:border-primary/50",
                  ].join(" ")}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={[
                      "text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full",
                      badgeCls,
                    ].join(" ")}>{status}</span>
                    {r.voice_verdict === "bad" && (
                      <span
                        data-testid={`review-row-voice-bad-${r.assistant_message_id}`}
                        className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-rose-600 text-white inline-flex items-center gap-1"
                        title={r.voice_reason || "Voice flagged as bad"}
                      >🔊 Voice bad</span>
                    )}
                    {(r.voice_played_count || 0) > 0 && r.voice_verdict !== "bad" && (
                      <span
                        data-testid={`review-row-voice-played-${r.assistant_message_id}`}
                        className="text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100"
                        title={`Read aloud ${r.voice_played_count} time(s)`}
                      >🔊 played · {r.voice_played_count}</span>
                    )}
                    <span className="text-[10px] font-mono text-muted-foreground truncate">{r.assistant_message_id}</span>
                  </div>
                  <p className="text-xs text-shell-foreground line-clamp-2">
                    <span className="text-muted-foreground">Q:</span> {r.prior_user_message?.content || <em>(no user turn)</em>}
                  </p>
                  <p className="text-xs text-shell-foreground line-clamp-2 mt-1">
                    <span className="text-muted-foreground">A:</span> {r.answer || <em>(no answer — provider error)</em>}
                  </p>
                  {r.current_reason && <p className="text-[10px] text-shell-foreground line-clamp-1 mt-1 italic">Reason: {r.current_reason}</p>}
                  {r.voice_reason && (
                    <p className="text-[10px] text-rose-700 line-clamp-1 mt-1 italic"
                       data-testid={`review-row-voice-reason-${r.assistant_message_id}`}>
                      🔊 Voice: {r.voice_reason}
                    </p>
                  )}
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Account {r.account_id} · {ts ? new Date(ts).toLocaleString() : "—"}
                  </p>
                </button>
              );
            })}
          </div>

          {/* Detail + edit panel */}
          <div className="surface p-4" data-testid="review-detail">
            {!context && <p className="text-sm text-muted-foreground">Select a review to see the assistant answer and revise the verdict.</p>}
            {context && (
              <>
                <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Founder's question</p>
                <p className="text-sm mt-1 mb-3 text-shell-foreground whitespace-pre-wrap">
                  {context.prior_user_message?.content || <em className="text-muted-foreground">(no preceding user turn)</em>}
                </p>
                <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Assistant answer</p>
                <p className="text-sm mt-1 mb-3 text-shell-foreground whitespace-pre-wrap" data-testid="review-assistant-body">
                  {context.assistant_message?.content}
                </p>
                {(context.assistant_message?.citations || []).length > 0 && (
                  <div className="mb-3">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Citations</p>
                    <ul className="text-xs text-shell-foreground space-y-0.5">
                      {(context.assistant_message.citations || []).map((c, i) => (
                        <li key={i} data-testid={`review-citation-${i}`}>
                          <span className="font-mono text-[11px] text-muted-foreground">{c.source_ref || c.document_id}</span> — {c.title}
                          {c.account_id && <span className="text-[10px] text-muted-foreground ml-1">· acct {c.account_id}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mt-4 mb-1">Reason</p>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  placeholder="What made this answer good or bad? (required for Bad)"
                  data-testid="review-reason"
                  className="w-full rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary"
                />
                <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mt-3 mb-1">
                  Proposed correction <span className="opacity-60">(optional — feeds the knowledge base when you Promote)</span>
                </p>
                <textarea
                  value={correction}
                  onChange={(e) => setCorrection(e.target.value)}
                  rows={4}
                  placeholder="What should Morgan have said instead? Full-sentence, conversational."
                  data-testid="review-correction"
                  className="w-full rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary"
                />
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <button onClick={() => submit("good")} disabled={saving}
                    data-testid="review-submit-good"
                    className="btn-primary text-xs inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ThumbsUp className="w-3.5 h-3.5" />} Mark good
                  </button>
                  <button onClick={() => submit("bad")} disabled={saving}
                    data-testid="review-submit-bad"
                    className="btn-primary text-xs inline-flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 disabled:opacity-60">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ThumbsDown className="w-3.5 h-3.5" />} Mark bad
                  </button>
                </div>

                {/* Promote to KB — turns the reviewed answer (Good) or
                    the proposed correction (Bad) into a shared kb_documents
                    entry so Morgan can retrieve it next time. */}
                <div className="mt-4 pt-3 border-t border-shell-border">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5">
                    Promote reviewed content into the knowledge base
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={promoteDomain}
                      onChange={(e) => setPromoteDomain(e.target.value)}
                      data-testid="review-promote-domain"
                      className="text-xs rounded-md border border-shell-border px-2 py-1.5 focus:outline-none focus:border-primary bg-white"
                    >
                      <option value="">— Unclassified (send to review queue) —</option>
                      {domains.map((d) => (
                        <option key={d.slug} value={d.slug}>{d.name} ({d.slug})</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={promoteToKb}
                      disabled={promoting}
                      data-testid="review-promote-btn"
                      className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-60"
                    >
                      {promoting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                      Promote to KB
                    </button>
                    {context.my_review?.promoted_kb_id && (
                      <span className="text-[10px] text-emerald-700 uppercase tracking-widest font-bold">
                        ✓ already promoted
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1.5">
                    Good ratings promote Morgan's own answer as a curated example.
                    Bad ratings promote your proposed correction. The new KB doc
                    is tagged with the reviewer, the message id, and the prompt version.
                  </p>
                </div>
                {context.my_review && (
                  <p className="text-[10px] text-muted-foreground mt-3">
                    Your last verdict: <strong className="uppercase">{context.my_review.verdict || "voice-only"}</strong> · {new Date(context.my_review.updated_at).toLocaleString()}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
